import { useCallback, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api from '@/lib/api';
import type { TodoAssignmentNotificationsResponse } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

const TODO_LISTS_ARCHE_ID = 'arche_todo_lists';

export interface UseTodoAssignmentNotificationsReturn {
  /** Whether the notifications query has resolved at least once. Lets a caller
   *  wait for real data before snapshotting the unread sets (see the list
   *  detail's "seen on open" freeze). */
  loaded: boolean;
  /** Item ids the caller has an unread assignment notification for. */
  unreadItemIds: Set<string>;
  /** Section ids the caller has an unread assignment notification for. */
  unreadSectionIds: Set<string>;
  /** Item ids the caller has an unread "review requested" notification for. */
  unreadReviewItemIds: Set<string>;
  /** Unread count for a given list (assignments + reviews). */
  countForList: (listId: string) => number;
  /**
   * Mark a single item/section assignment read (the "I clicked it" action).
   * Optimistically drops the dot, then persists and refreshes the header bell.
   */
  markAssignmentRead: (kind: 'item' | 'section', targetId: string) => void;
  /**
   * Mark a single "review requested" notification read — fired when the reviewer
   * opens the item. Uses the namespaced `review:<itemId>` group key so it clears
   * only the review dot, never the assignment dot on the same item.
   */
  markReviewRead: (itemId: string) => void;
  /**
   * Mark ALL of a list's notifications read at once — assignments, section
   * grants, and review requests — fired when the caller OPENS the list ("seen on
   * open"). Clears the nav / worktop badge and the per-list badge in one shot,
   * keyed by list rather than by clicking each rendered dot, so a notification
   * whose dot never renders (a completed/cancelled item dropped from the active
   * view, a section grant) still clears. The list detail keeps the dots painted
   * for the visit from a local snapshot. No-op when the list has no unread.
   */
  markListNotificationsRead: (listId: string) => void;
}

/**
 * The caller's unread assignment notifications across every workspace, used to
 * paint per-list badges (My Lists) and per-item unread dots (list detail).
 *
 * Enabled only when authenticated. Clearing is strictly per-item: viewing one
 * assignment never touches another list's or workspace's notifications.
 */
export function useTodoAssignmentNotifications(
  enabled = true,
): UseTodoAssignmentNotificationsReturn {
  const queryClient = useQueryClient();
  // Stable across renders so the callbacks below (and effects that depend on
  // them, e.g. the list-load review clear) don't churn each render.
  const key = useMemo(() => queryKeys.todoLists.assignmentNotifications(), []);

  const { data, isSuccess } = useQuery({
    queryKey: key,
    queryFn: () => api.getTodoAssignmentNotifications(),
    enabled,
    // Poll so the dots reflect assignment changes made by OTHER users (e.g. a
    // manager reassigning your task away) without a manual refresh — the same
    // freshness the header bell gets. Pauses while the tab is hidden.
    refetchInterval: 30_000,
  });

  const unreadItemIds = useMemo(() => new Set((data?.items ?? []).map((i) => i.itemId)), [data]);
  const unreadSectionIds = useMemo(
    () => new Set((data?.sections ?? []).map((s) => s.sectionId)),
    [data],
  );
  const unreadReviewItemIds = useMemo(
    () => new Set((data?.reviews ?? []).map((r) => r.itemId)),
    [data],
  );

  const countForList = useCallback(
    (listId: string) => {
      const items = data?.items.filter((i) => i.listId === listId).length ?? 0;
      const sections = data?.sections.filter((s) => s.listId === listId).length ?? 0;
      const reviews = data?.reviews?.filter((r) => r.listId === listId).length ?? 0;
      return items + sections + reviews;
    },
    [data],
  );

  const markAssignmentRead = useCallback(
    (kind: 'item' | 'section', targetId: string) => {
      // Optimistically remove the target so the dot clears immediately.
      queryClient.setQueryData<TodoAssignmentNotificationsResponse>(key, (old) => {
        if (!old) return old;
        return kind === 'item'
          ? { ...old, items: old.items.filter((i) => i.itemId !== targetId) }
          : { ...old, sections: old.sections.filter((s) => s.sectionId !== targetId) };
      });
      // Persist, then refresh the cross-arche badge (header bell / worktop tile).
      // Best-effort: on failure, re-fetch to resync the optimistic drop.
      void api
        .markNotificationsRead(TODO_LISTS_ARCHE_ID, targetId)
        .then(() => queryClient.invalidateQueries({ queryKey: queryKeys.notifications.summary() }))
        .catch(() => queryClient.invalidateQueries({ queryKey: key }));
    },
    [queryClient, key],
  );

  const markReviewRead = useCallback(
    (itemId: string) => {
      queryClient.setQueryData<TodoAssignmentNotificationsResponse>(key, (old) =>
        old ? { ...old, reviews: old.reviews.filter((r) => r.itemId !== itemId) } : old,
      );
      // The server keys review dots on the namespaced "review:<itemId>" group key.
      void api
        .markNotificationsRead(TODO_LISTS_ARCHE_ID, `review:${itemId}`)
        .then(() => queryClient.invalidateQueries({ queryKey: queryKeys.notifications.summary() }))
        .catch(() => queryClient.invalidateQueries({ queryKey: key }));
    },
    [queryClient, key],
  );

  const markListNotificationsRead = useCallback(
    (listId: string) => {
      const current = queryClient.getQueryData<TodoAssignmentNotificationsResponse>(key);
      if (!current) return;
      const itemIds = current.items.filter((i) => i.listId === listId).map((i) => i.itemId);
      const sectionIds = current.sections
        .filter((s) => s.listId === listId)
        .map((s) => s.sectionId);
      const reviewItemIds = (current.reviews ?? [])
        .filter((r) => r.listId === listId)
        .map((r) => r.itemId);
      if (itemIds.length + sectionIds.length + reviewItemIds.length === 0) return;
      // Optimistically drop this list's notifications so the nav + per-list
      // badges settle immediately. The list detail keeps the DOTS painted for the
      // visit from its own snapshot — this only clears the counts.
      queryClient.setQueryData<TodoAssignmentNotificationsResponse>(key, (old) =>
        old
          ? {
              ...old,
              items: old.items.filter((i) => i.listId !== listId),
              sections: old.sections.filter((s) => s.listId !== listId),
              reviews: old.reviews.filter((r) => r.listId !== listId),
            }
          : old,
      );
      // Persist each. Assignment/section dots key on the raw item/section id;
      // review dots on the namespaced "review:<itemId>". Refresh the cross-arche
      // badge once. Best-effort: on failure, re-fetch to resync the optimistic drop.
      void Promise.all([
        ...itemIds.map((id) => api.markNotificationsRead(TODO_LISTS_ARCHE_ID, id)),
        ...sectionIds.map((id) => api.markNotificationsRead(TODO_LISTS_ARCHE_ID, id)),
        ...reviewItemIds.map((id) =>
          api.markNotificationsRead(TODO_LISTS_ARCHE_ID, `review:${id}`),
        ),
      ])
        .then(() => queryClient.invalidateQueries({ queryKey: queryKeys.notifications.summary() }))
        .catch(() => queryClient.invalidateQueries({ queryKey: key }));
    },
    [queryClient, key],
  );

  return {
    loaded: isSuccess,
    unreadItemIds,
    unreadSectionIds,
    unreadReviewItemIds,
    countForList,
    markAssignmentRead,
    markReviewRead,
    markListNotificationsRead,
  };
}
